import React from 'react';
import { Box, Divider, ToggleButton } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type {
  Questionary,
  ScriptStep,
} from '@shared/interfaces/assistantScripts';
import {
  SectionLabelText,
  ScriptToggleButtonGroup,
} from '../AssistantScriptDialog.styled';
import MultipleChoiceEditor from './MultipleChoiceEditor';
import TextInputEditor from './TextInputEditor';

type MultipleChoiceQuestionary = Extract<
  Questionary,
  { type: 'multiple_choice' }
>;

interface QuestionarySectionProps {
  step: ScriptStep;
  index: number;
  stepRef: HTMLDivElement | null | undefined;
  onUpdateStep: (data: Partial<ScriptStep>) => void;
}

const QuestionarySection = ({
  step,
  index,
  stepRef,
  onUpdateStep,
}: QuestionarySectionProps) => {
  const { t } = useTranslation('common');
  const handleTypeChange = (_: React.SyntheticEvent, value: string | null) => {
    if (value === null) {
      return;
    }

    if (value === 'multiple_choice') {
      onUpdateStep({
        questionary: {
          type: 'multiple_choice',
          selectionMode: 'single',
          options: ['', ''],
          correctAnswers: [],
        },
      });
      setTimeout(
        () => stepRef?.scrollIntoView({ behavior: 'smooth', block: 'end' }),
        100,
      );
    } else if (value === 'text_input') {
      onUpdateStep({ questionary: { type: 'text_input' } });
    } else {
      onUpdateStep({ questionary: { type: 'none' } });
    }
  };

  const handleMultipleChoiceUpdate = (q: MultipleChoiceQuestionary) => {
    onUpdateStep({ questionary: q });
  };

  return (
    <>
      <Divider sx={{ borderColor: 'grey.100' }} />
      <Box>
        <SectionLabelText>{t('assistant.scriptDialog.questionType')}</SectionLabelText>
        <ScriptToggleButtonGroup
          value={step.questionary?.type ?? 'none'}
          exclusive
          onChange={handleTypeChange}
          size="small"
          sx={{ mb: 1.5, flexWrap: 'wrap' }}
        >
          <ToggleButton
            value="none"
            data-testid={`script-step-${index}-question-type-none`}
          >
            {t('assistant.scriptDialog.questionNone')}
          </ToggleButton>
          <ToggleButton
            value="multiple_choice"
            data-testid={`script-step-${index}-question-type-multipleChoice`}
          >
            {t('assistant.scriptDialog.questionMultipleChoice')}
          </ToggleButton>
        </ScriptToggleButtonGroup>

        {step.questionary?.type === 'multiple_choice' && (
          <MultipleChoiceEditor
            questionary={step.questionary}
            index={index}
            onUpdate={handleMultipleChoiceUpdate}
          />
        )}

        {step.questionary?.type === 'text_input' && <TextInputEditor />}
      </Box>
    </>
  );
};

export default QuestionarySection;
